// EN mirror of /search — client-side search across EN pages
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Search as SearchIcon } from "lucide-react";
import { useState, useMemo } from "react";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { BRAND_URL } from "@/lib/brand";

const TITLE = "Search — Translation, Apostille & Thai Visa | NYC Language Institute";
const DESC = "Search across every English page of NYC Language Institute — apostille, MFA legalization, notary. Start with a free review before you file anything.";

const INDEX = [
  { title: "Apostille Thailand — MFA legalization", path: "/en/apostille", tags: "apostille mfa legalization hague" },
  { title: "Certified translation", path: "/en/translate", tags: "translation certified sworn" },
  { title: "Notary public services", path: "/en/notary", tags: "notary notarial public act" },
  { title: "NAATI translation", path: "/en/naati", tags: "naati australia certified" },
  { title: "Thai visa services", path: "/en/thai-visa", tags: "visa non-b ltr dtv retirement marriage" },
  { title: "BOI application", path: "/en/corporate/boi-application", tags: "boi corporate promotion" },
  { title: "Company registration", path: "/en/corporate/company-registration", tags: "company setup registration" },
  { title: "Marriage abroad", path: "/en/marriage-abroad", tags: "marriage foreign spouse legalization" },
  { title: "Police clearance", path: "/en/apostille/police-clearance", tags: "police clearance criminal record" },
  { title: "Blog", path: "/en/blog", tags: "blog articles news" },
  { title: "Case studies", path: "/en/case-studies", tags: "cases outcomes projects" },
  { title: "Citations", path: "/en/citations", tags: "sources references gov" },
  { title: "Guides", path: "/en/guides", tags: "guides handbooks walkthrough" },
  { title: "Contact", path: "/en/contact", tags: "contact line email phone" },
  { title: "Checklist", path: "/en/checklist", tags: "checklist documents required" },
  { title: "Sample works", path: "/en/samples", tags: "samples portfolio examples" },
  { title: "Privacy policy", path: "/en/privacy", tags: "privacy pdpa gdpr" },
  { title: "Terms of service", path: "/en/terms", tags: "terms tos legal" },
];

export const Route = createFileRoute("/en/search")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/search", "en")}, { name: "description", content: ctaDesc(DESC, "/en/search", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/search", "en")}, { property: "og:description", content: ctaDesc(DESC, "/en/search", "en")},
      { property: "og:url", content: `${BRAND_URL}/en/search` }, { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${BRAND_URL}/en/search` },
      ...hreflangLinks("/search", "/en/search"),
    ],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org", "@type": "WebSite",
        url: `${BRAND_URL}/en`, name: "NYC Language Institute",
        potentialAction: {
          "@type": "SearchAction",
          target: { "@type": "EntryPoint", urlTemplate: `${BRAND_URL}/en/search?q={query}` },
          "query-input": "required name=query",
        },
      }),
    }],
  }),
  component: EnSearch,
});

function EnSearch() {
  const [q, setQ] = useState("");
  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return INDEX;
    return INDEX.filter((r) => (r.title + " " + r.tags).toLowerCase().includes(s));
  }, [q]);

  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep px-4 py-14 text-white">
        <div className="mx-auto max-w-3xl">
          <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-4xl">Search</h1>
          <label className="mt-4 flex items-center gap-2 rounded-2xl bg-white/10 px-4 py-3 backdrop-blur">
            <SearchIcon className="h-5 w-5 text-amber-300" aria-hidden />
            <input
              autoFocus type="search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="apostille, LTR, notary, NAATI, BOI…"
              aria-label="Search NYC EN site"
              className="w-full bg-transparent text-white placeholder-white/50 outline-none"
            />
          </label>
        </div>
      </section>
      <section className="px-4 py-12">
        <div className="mx-auto max-w-3xl">
          <p className="mb-4 text-xs text-slate-500">{results.length} result{results.length === 1 ? "" : "s"}</p>
          <ul className="space-y-2">
            {results.map((r) => (
              <li key={r.path}>
                <Link to={r.path} className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-amber-400 hover:bg-amber-50/40">
                  <div className="text-base font-bold text-navy-deep">{r.title}</div>
                  <div className="text-[11px] text-slate-500">{r.path}</div>
                </Link>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-xs text-slate-500">Thai search: <Link to="/search" search={{ q: "", cat: "" }} className="text-amber-700 underline">/search</Link></p>
        </div>
      </section>
    <RelatedHubGrid locale="en" /></main>
  );
}
